import React, { useCallback, useEffect, useMemo } from 'react';
import { Button, CustomSelect, CustomSelectItem, LabeledValue } from '@hwm/ui-lib';
import { makeStyles, Menu, Theme, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { isEmpty } from 'lodash';
import { DigitalIntervalOptionsFn, FilterUnitsPostModel, IntervalOptionMode } from '@shared/models';
import { useGraphFilterState } from '@features/DataView/useGraphFilterState';
import { Trans } from '@lingui/macro';

const SELECTOR_WIDTH = 128;

const useStyles = makeStyles((theme: Theme) => ({
  labeledValue: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: theme.spacing(0),
    },
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      marginBottom: theme.spacing(3),
    },
  },
  select: {
    height: 32,
    maxHeight: 32,
    width: SELECTOR_WIDTH,
    maxWidth: SELECTOR_WIDTH,
    zIndex: 200,
    '& .select__control': {
      minWidth: 84,
    },
  },
}));

export const GraphUnits: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  useEffect(() => setAnchorEl(null), [smallScreen]);

  const { graphState, setUnitsFilter } = useGraphFilterState();
  const {
    units,
    channelTypeUnitsAndPercentage: { channelTypeUnitOptions },
  } = graphState;

  const unitsState: FilterUnitsPostModel = useMemo(
    () => Object.fromEntries(units.map((el) => [el.type, { name: el.unit, interval: el.interval ?? null }])),
    [units]
  );

  const handleOnChange = useCallback(
    (type: string, selectedValue: CustomSelectItem, asUnit: boolean) => {
      const newState = { ...unitsState };
      newState[type][asUnit ? 'name' : 'interval'] = selectedValue.value;
      setUnitsFilter(
        Object.entries(newState).map(([type, { name, interval }]) => ({
          type,
          unit: name,
          interval,
          intervalMode: graphState?.units?.find((u) => u.type === type)?.intervalMode ?? IntervalOptionMode.None,
        }))
      );
    },
    [unitsState, graphState.units, setUnitsFilter]
  );

  const showSelectors = useMemo(() => !isEmpty(unitsState), [unitsState]);

  const selectors = useMemo(() => {
    return showSelectors ? (
      <div className="flex flex-col" data-testid="main-filters-graph-units-selectors">
        <Typography
          className="hover:text-primaryColor cursor-pointer"
          onClick={() => setAnchorEl(null)}
          style={{ marginLeft: 'auto', marginBottom: 4 }}
        >
          <Trans>OK</Trans>
        </Typography>
        {channelTypeUnitOptions?.map(({ type, units, intervals }) => (
          <LabeledValue
            key={type + units}
            label={type}
            className={classes.labeledValue}
            dataTestId="main-filters-graph-units-selector-labeled-value"
          >
            <div className="flex flex-row items-center mt-0.5 sm:mt-0">
              <CustomSelect
                label={null}
                placeholder=""
                onChange={(selectedValue) => handleOnChange(type, selectedValue as CustomSelectItem, true)}
                options={
                  units?.map(({ name }) => ({
                    label: name.replace('Â', ''),
                    value: name,
                  })) ?? []
                }
                minWidth={84}
                className={classes.select}
                value={{
                  label: unitsState[type]?.name?.replace('Â', ''),
                  value: unitsState[type]?.name,
                }}
                isDisabled={false}
                isError={false}
                newEditableLook
                dataTestId="main-filters-graph-units-selector-labeled-value-units-select"
              />
              <div className="text-2xl mx-2" style={{ width: 8 }}>
                {graphState?.units?.find((x) => x.type === type)?.intervalMode === IntervalOptionMode.FlowRate && '/'}
              </div>
              {unitsState[type]?.interval ? (
                <>
                  <CustomSelect
                    label={null}
                    placeholder=""
                    onChange={(selectedValue) => handleOnChange(type, selectedValue as CustomSelectItem, false)}
                    options={
                      intervals?.map(({ interval }) => ({
                        label: DigitalIntervalOptionsFn()[interval],
                        value: interval,
                      })) ?? []
                    }
                    minWidth={84}
                    className={classes.select}
                    value={{
                      label: unitsState[type]?.interval,
                      value: unitsState[type]?.interval,
                    }}
                    isDisabled={false}
                    isError={false}
                    newEditableLook
                    dataTestId="main-filters-graph-units-selector-labeled-value-interval-select"
                  />
                </>
              ) : (
                <div style={{ width: SELECTOR_WIDTH }}></div>
              )}
            </div>
          </LabeledValue>
        ))}
      </div>
    ) : null;
  }, [channelTypeUnitOptions, unitsState, graphState.units, showSelectors, classes, handleOnChange]);

  return (
    <div data-testid="main-filters-graph-units">
      <Button
        style={{
          height: smallScreen ? '100%' : 32,
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
        disabled={isEmpty(graphState.units)}
        dataTestId="main-filters-graph-units-button"
      >
        <Trans>Units</Trans>
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: smallScreen ? 'left' : 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: smallScreen ? 'left' : 'center',
        }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{ paper: 'px-2 pb-1 pt-0' }}
        PaperProps={{
          style: { maxHeight: 260 },
        }}
        BackdropProps={{ style: { backgroundColor: 'black', opacity: 0.4 } }}
      >
        {selectors}
      </Menu>
    </div>
  );
};
